﻿import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { MenuItem, Intent, Spinner } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import { Suggest } from "@blueprintjs/select";
import PropTypes from 'prop-types';
import { useDebounce } from 'hooks/useDebounce';

import { FormHelperText } from "components/form-fields";

import 'components/form-fields/FormFields.css';
import _ from 'underscore';

const defaultArray = [];
const scrollableMenuProps = { className: "form-suggest-scrollable-menu" };

export function FormSuggest(
    {
        id = null,
        items = defaultArray,
        selectedValue = null,
        disabled = false,
        loading = false,
        headingText = "",
        helperText = null,
        dangerHelperText = null,
        clearSelection = true,
        fullWidth = false,
        onFilter = null,
        debounceTime = 100,
        large = false,
        onQueryChange = null,
        itemsToReturn = 30,
        resetOnSelect = true,
        allowCreateItem = false,
        getDynamicItems = null,
        inputRef = null,
        scrollableMenu = false,
        onItemSelect
    }
) {
	const [itemsValue, setItems] = useState([]);
	const [selectedItem, setSelectedItem] = useState(null);

	const [currentQuery, setCurrentQuery] = useState("");
	const debouncedQuery = useDebounce(currentQuery, debounceTime);

	const [externalFiltering, setExternalFiltering] = useState(false);
	const isExternalFiltering = typeof onFilter === "function";
	const isFiltering = debouncedQuery.length > 0;

	// Internal Filtering
	const filteredItems = useMemo(() => {

		const toSlice = (list) => list.slice(0, itemsToReturn);

		if (isExternalFiltering || !isFiltering) {
			return toSlice(itemsValue);
		}

		return toSlice(_.filter(itemsValue, function (i) {
			const lowerName = i.name?.toLowerCase() ?? "";
			return lowerName.indexOf(debouncedQuery.toLowerCase()) > -1;
		}));
	}, [itemsValue, debouncedQuery]);

	// External Filtering
	useEffect(() => {
		if (!isExternalFiltering || !isFiltering) {
			return;
		}

		setExternalFiltering(true);
		onFilter(debouncedQuery);
	}, [debouncedQuery]);

	// Update items if props.items changes
	useEffect(() => {
		setItems(items);
		setExternalFiltering(false);
	}, [items]);

	function handleQueryChange(query) {
		if (typeof onQueryChange === "function") {
			onQueryChange(query);
		} else {
			setCurrentQuery(query);
		}
	}

	function handleSelectedItem(i) {
		var newItem = i.find(obj => String(obj.id) === String(selectedValue)) ?? null;
		setSelectedItem(newItem);
	}

	function renderInputValue(item) {
		return item.name;
	}

	function renderSelect(data, { handleClick, modifiers }) {
		return (
			<MenuItem
				active={modifiers.active}
				onClick={handleClick}
				key={data.id}
				text={data.name}
				disabled={modifiers.disabled || data.disabled}
				icon={data.icon}
			/>
		);
	}

	function handleNewItem(query) {
		const idValue = _.uniqueId("new-item-");
		const name = query.trim().trimLeft();

		if (name.length === 0) {
			return null;
		}

		return renderSelect({
			id: idValue,
			name,
		}, {
			handleClick: () => onItemSelect({ id: idValue, name }),
			modifiers: { active: true, disabled: false },
		});
	}

	function clearItem() {
		onItemSelect({ id: null });
	}

	useEffect(() => handleSelectedItem(itemsValue), [selectedValue, itemsValue]);

	useEffect(() => {
		getDynamicItems?.().then(result => {
			setItems(result);
			handleSelectedItem(result);
		}).finally(() => setExternalFiltering(false));
	}, [getDynamicItems]);

	return (
        <div className={classNames("form-field form-field-suggest", { "suggest-clearable": clearSelection })} id={id}>
            <ShowHide
				evaluator={headingText.length > 0}
				show={<h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>}
			/>
            <FormHelperText loading={loading}>{helperText}</FormHelperText>
            <Suggest
				disabled={disabled}
				className={classNames({ "bp6-skeleton": loading })}
				items={filteredItems}
				itemRenderer={renderSelect}
				inputValueRenderer={renderInputValue}
				onItemSelect={onItemSelect}
				inputProps={{ large: large, inputRef: inputRef }}
				popoverProps={{ minimal: true, usePortal: false }}
				menuProps={scrollableMenu ? scrollableMenuProps : undefined}
				fill={fullWidth}
				onQueryChange={handleQueryChange}
				selectedItem={selectedItem}
				resetOnSelect={resetOnSelect}
				createNewItemPosition='first'
				createNewItemFromQuery={() => allowCreateItem}
				createNewItemRenderer={allowCreateItem ? handleNewItem : null}
			/>
            <ShowHide
				evaluator={selectedItem != null && clearSelection}
				show={(
					<Button loading={loading} text="Clear" intent={Intent.DANGER} minimal={true} large={false} icon="cross" onClick={clearItem} disabled={disabled || externalFiltering}></Button>
				)}
			></ShowHide>
            <ShowHide
				evaluator={externalFiltering}
				show={(
					<div className="save-loader">
						<Spinner size={25} />
					</div>
				)}
			/>
            <FormHelperText loading={loading} danger={true}>{dangerHelperText}</FormHelperText>
        </div>
    );
}

FormSuggest.propTypes = {
	id: PropTypes.string,
	selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	items: PropTypes.array,
	disabled: PropTypes.bool,
	onItemSelect: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	headingText: PropTypes.string,
	helperText: PropTypes.node,
	dangerHelperText: PropTypes.node,
	clearSelection: PropTypes.bool,
	fullWidth: PropTypes.bool,
	onFilter: PropTypes.func,
	debounceTime: PropTypes.number,
	large: PropTypes.bool,
	onQueryChange: PropTypes.func, //If set it means the object consuming this needs to pass a method to run when data being entered changes
	itemsToReturn: PropTypes.number,
	resetOnSelect: PropTypes.bool,
	allowCreateItem: PropTypes.bool,
	getDynamicItems: PropTypes.func,
	inputRef: PropTypes.object,
	scrollableMenu: PropTypes.bool
};